import { format, parseISO } from "date-fns"

import { formatDayMonth, formatDayMonthYear } from "@/lib/dateFormats"
import type { OperatorAllocation } from "@/types/collections"

const dateKey = (date: Date | string): string =>
  typeof date === "string" ? date.slice(0, 10) : format(date, "yyyy-MM-dd")

/** The allocation covering a date, if any. Ranges are inclusive and never overlap. */
export function allocationOn(
  allocations: OperatorAllocation[] | undefined,
  date: Date | string,
): OperatorAllocation | undefined {
  const day = dateKey(date)
  return allocations?.find((a) => a.from_date <= day && day <= a.to_date)
}

/**
 * Who runs a route on a date: the operator it is handed to, else the plan's own. Undefined on a
 * partner's board for dates handed to another company.
 */
export function operatorOn(
  route: { operator?: string; operatorAllocations?: OperatorAllocation[] },
  date: Date | string,
): string | undefined {
  const allocation = allocationOn(route.operatorAllocations, date)
  if (allocation) return allocation.operator ?? undefined
  return route.operator
}

/** "2 Nov – 15 Nov 2026", or one date where the range is a single day. */
export function formatAllocationDates(allocation: { from_date: string; to_date: string }): string {
  const from = parseISO(allocation.from_date)
  const to = parseISO(allocation.to_date)
  return allocation.from_date === allocation.to_date
    ? formatDayMonthYear(from)
    : `${formatDayMonth(from)} – ${formatDayMonthYear(to)}`
}
